/**
 * Memory Panel Component - מה יועצי זוכר עליי
 * Lets a user view and clear the cross-conversation profile the AI builds
 * about them. GDPR transparency + right-to-erasure.
 */

import { useEffect, useState } from 'react'
import { Brain, X, Trash2, Loader2, AlertCircle, Pencil } from 'lucide-react'
import api, { handleApiError } from '../../services/api'
import MemoryEditor, { MemoryPayload, MemoryPatch } from './MemoryEditor'

interface MemoryPanelProps {
  isOpen: boolean
  onClose: () => void
}

const EMPTY_MEMORY: MemoryPayload = {
  enabled: true,
  autoUpdateEnabled: true,
  hasMemory: false,
  summary: '',
  topics: [],
  structured: { keyFacts: [], preferences: [], concerns: [] },
  lastSummarizedAt: null,
}

export default function MemoryPanel({ isOpen, onClose }: MemoryPanelProps) {
  const [memory, setMemory] = useState<MemoryPayload | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setIsEditing(false)
      loadMemory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const loadMemory = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await api.get('/chat/memory')
      setMemory(res.data?.memory ?? null)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async (patch: MemoryPatch) => {
    setIsSaving(true)
    setError(null)
    try {
      const res = await api.put('/chat/memory', patch)
      setMemory(res.data?.memory ?? null)
      setIsEditing(false)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsSaving(false)
    }
  }

  const handleClear = async () => {
    if (!window.confirm('למחוק את כל מה שיועצי זוכר עליך? פעולה זו אינה הפיכה.')) return
    setIsClearing(true)
    setError(null)
    try {
      await api.delete('/chat/memory')
      await loadMemory()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsClearing(false)
    }
  }

  if (!isOpen) return null

  const hasContent = memory?.hasMemory && memory.summary
  const structured = memory?.structured

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="memory-panel-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Brain size={20} className="text-primary-600" aria-hidden="true" />
            <h2 id="memory-panel-title" className="text-base font-heading font-bold text-gray-900">
              מה יועצי זוכר עליי
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="סגור"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500">
            יועצי זוכר פרטים מהשיחות שלך כדי לתת מענה אישי יותר ולא לשאול שוב על מה שכבר סיפרת.
            אתה יכול למחוק את הזיכרון בכל עת.
          </p>

          {isLoading && (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 size={22} className="animate-spin" aria-hidden="true" />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700" role="alert">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p>{error}</p>
            </div>
          )}

          {!isLoading && isEditing && (
            <MemoryEditor
              initial={memory ?? EMPTY_MEMORY}
              saving={isSaving}
              error={error}
              onSave={handleSave}
              onCancel={() => {
                setIsEditing(false)
                setError(null)
              }}
            />
          )}

          {!isLoading && !error && !isEditing && !hasContent && (
            <div className="text-center py-8 text-gray-500 text-sm">
              <p>יועצי עדיין לא צבר זיכרון עליך. ככל שתשוחח יותר, המענה יהפוך אישי יותר.</p>
              <button onClick={() => setIsEditing(true)} className="btn btn-outline text-sm mt-4">
                הוסף העדפות בעצמך
              </button>
            </div>
          )}

          {!isLoading && !error && !isEditing && hasContent && (
            <>
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">סיכום</h3>
                <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {memory!.summary}
                </p>
              </div>

              {memory!.topics.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-1.5">נושאים</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {memory!.topics.map((t, i) => (
                      <span
                        key={i}
                        className="inline-block bg-primary-50 text-primary-700 text-xs rounded-full px-2.5 py-1"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <StructuredList title="עובדות" items={structured?.keyFacts} />
              <StructuredList title="העדפות" items={structured?.preferences} />
              <StructuredList title="דגשים" items={structured?.concerns} />

              {memory!.lastSummarizedAt && (
                <p className="text-xs text-gray-400">
                  עודכן לאחרונה: {new Date(memory!.lastSummarizedAt).toLocaleDateString('he-IL')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!isLoading && !isEditing && hasContent && (
          <div className="px-5 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => setIsEditing(true)}
              className="btn btn-outline text-sm flex items-center gap-1.5"
            >
              <Pencil size={15} aria-hidden="true" />
              ערוך
            </button>
            <button
              onClick={handleClear}
              disabled={isClearing}
              className="btn btn-outline text-sm text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-1.5"
            >
              {isClearing ? (
                <Loader2 size={15} className="animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 size={15} aria-hidden="true" />
              )}
              מחק זיכרון
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function StructuredList({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-1">{title}</h3>
      <ul className="list-disc pr-5 space-y-0.5 text-sm text-gray-700">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  )
}
